import './UserAssessmentComplete.scss'

import { useEffect, useState } from 'react'
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap'

import {
  getSavedUserAssessmentData,
  getSavedUserAssessmentPlan
} from 'components/common/api/Users'
import RembleLogomark from '../../../assets/images/Remble_Logomark-Green.png'
import { navigateTo } from 'components/common/navigation'
import { ROUTE_ASSESSMENT_PROGRAM } from 'components/common/helpers/Paths'
import { useHistory } from 'react-router-dom'
import AssessmentStatusCard from './AssessmentStatusCard'
import UserProgramCourse from '../UserProgram/UserProgramCourse'
// import UserProgramPractice from '../UserProgram/UserProgramPractice'
import { toggleTabBar } from 'components/common/helpers/Mobile'
import useConfetti from 'components/common/hooks/useConfetti'

/**
 * User Assessment Complete component: used to process the user assessment which customizes the user experience
 */
export default function UserAssessmentComplete({ handleComplete }) {
  const history = useHistory()

  const [userProgram, setUserProgram] = useState()
  const [savedAssessment, setSavedAssessment] = useState()

  const [showPlan, setShowPlan] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState()

  // Show confetti when the assessment is complete
  useConfetti(!isLoading && !error && !showPlan && isReady)

  useEffect(() => {
    // Return early if we already have the assessment data
    if (isReady) return

    Promise.allSettled([
      // Get the user's assessment data
      getSavedUserAssessmentData().then(result => setSavedAssessment(result)),

      // Get the user's assessment plan
      getSavedUserAssessmentPlan().then(result => setUserProgram(result))
    ])
      .catch(error => {
        setError(error)
      })
      .finally(_ => {
        setIsLoading(false)
        setIsReady(true)
      })
  }, [isReady])

  return (
    <Container className='user-assessment-container complete'>
      {/* Did we receive an error? */}
      {error && (
        <div className='error'>
          <i className='icon bi-exclamation-circle'></i>{' '}
          <strong>Error: </strong> {' ' + error}
        </div>
      )}

      <Row className='user-assessment'>
        <Col xs={12}>
          <img
            className='logomark'
            src={RembleLogomark}
            alt='Remble'
            width='25'
            fetchpriority='high'
          />
        </Col>

        {showPlan ? (
          <>
            <Col>
              {isReady && (
                <div className='assessment-text'>
                  <h4 className='green'>Your Goal for Today</h4>
                  <p>Complete at least one session from today's plan:</p>
                  <UserProgramCourse
                    courseId={userProgram?.courses?.split(',')[0]}
                    // If we have a complete handler, we are in preview mode
                    previewOnly={!!handleComplete}
                    showProgress={false}
                    className='my-4'
                    returnPath={ROUTE_ASSESSMENT_PROGRAM}
                  />

                  <h4>Tomorrow</h4>
                  <p>
                    Come back tomorrow and continue the next session in your
                    plan. You'll learn <em>even more</em> skills to help you
                    meet your goals
                  </p>
                </div>
              )}
            </Col>
            <Col xs={12}>
              <Button
                variant='primary'
                className='w-100 mt-5'
                disabled={isLoading}
                onClick={_ => {
                  // If we have a `handleComplete` function, call it
                  if (handleComplete) return handleComplete()

                  // Show the tab bar again
                  toggleTabBar('on')

                  // Otherwise, navigate to the assessment program
                  navigateTo(ROUTE_ASSESSMENT_PROGRAM, history)
                }}
              >
                See my full plan
              </Button>
            </Col>
          </>
        ) : (
          <>
            <Col xs={12}>
              {isLoading ? (
                <>
                  <h1 className='title mt-3'>We are almost there...</h1>
                  <p className='muted'>
                    We are crafting a unique and personal experience for you
                    based on your answers.
                  </p>
                </>
              ) : (
                <>
                  <h1 className='title mt-3'>
                    Your personalized plan is ready!
                  </h1>
                  <p className='muted'>
                    We have crafted a unique and personal experience for you
                    based on your answers.
                  </p>
                </>
              )}
            </Col>

            <Col xs={12}>
              {/* Status messages */}
              <AssessmentStatusCard show={!!savedAssessment}>
                Gathering your latest{' '}
                <strong className='green'>personal assessment</strong> details
              </AssessmentStatusCard>

              <AssessmentStatusCard show={!!userProgram}>
                Searching for <strong className='green'>sessions</strong> that
                meet your needs
              </AssessmentStatusCard>

              {isLoading && (
                <div className='text-center my-5'>
                  <Spinner variant='primary' animation='border' />
                  <p>Preparing your personalized plan...</p>
                </div>
              )}
            </Col>

            <Col xs={12}>
              <Button
                variant='primary'
                className='w-100 mt-5'
                disabled={isLoading}
                onClick={_ => setShowPlan(true)}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as='span'
                      animation='border'
                      size='sm'
                      role='status'
                      aria-hidden='true'
                    />
                    <span className='visually-hidden'>Loading...</span>
                  </>
                ) : (
                  'See my results'
                )}
              </Button>
            </Col>
          </>
        )}
      </Row>
    </Container>
  )
}
